import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone } from 'lucide-react';

const TourPackageCity = () => {
  const { cityName } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cityName]);

  const cityRoutes = {
    amritsar: [
      { destination: 'Agra', fare: 17000 },
      { destination: 'Pathankot', fare: 15000 },
      { destination: 'Dharamshala', fare: 11000 },
      { destination: 'Dalhousie', fare: 9000 },
      { destination: 'Ayodhya', fare: 30000 },
      { destination: 'Char Dham', fare: 31000 },
      { destination: 'Badrinath Kedarnath', fare: 31000 },
      { destination: 'Govind Ghat', fare: 30000 },
      { destination: 'Haridwar', fare: 13000 },
      { destination: 'Jaisalmer', fare: 20000 },
      { destination: 'Vrindavan', fare: 17000 },
      { destination: 'Vaishno Devi', fare: 11000 }
    ],
    chandigarh: [
      { destination: 'Dalhousie', fare: 9000 },
      { destination: 'Amarnath', fare: 18000 },
      { destination: 'Dharamshala', fare: 7000 },
      { destination: 'Shimla', fare: 5500 },
      { destination: 'Mussoorie', fare: 7000 },
      { destination: 'Nainital', fare: 13200 },
      { destination: 'Manali', fare: 7500 },
      { destination: 'Himachal Pradesh', fare: 7000 },
      { destination: 'Amritsar', fare: 7000 },
      { destination: 'Golden Temple Amritsar', fare: 7000 },
      { destination: 'Delhi Airport', fare: 6000 },
      { destination: 'Delhi', fare: 6000 }
    ],
    delhi: [
      { destination: 'Manali', fare: 14000 },
      { destination: 'Shimla', fare: 14000 },
      { destination: 'Dharamshala', fare: 14000 },
      { destination: 'Dalhousie', fare: 16000 },
      { destination: 'Mussoorie', fare: 11000 },
      { destination: 'Himachal Pradesh', fare: 14000 },
      { destination: 'Amritsar', fare: 12000 },
      { destination: 'Golden Temple Amritsar', fare: 12000 },
      { destination: 'Khatu Shyam', fare: 7000 },
      { destination: 'Salasar Balaji', fare: 7000 },
      { destination: 'Kasol', fare: 15000 }
    ],
    mohali: [
      { destination: 'Manali', fare: 7500 },
      { destination: 'Shimla', fare: 5000 },
      { destination: 'Dharamshala', fare: 7500 },
      { destination: 'Mussoorie', fare: 7000 },
      { destination: 'Himachal Pradesh', fare: 7000 },
      { destination: 'Amritsar', fare: 7000 },
      { destination: 'Golden Temple Amritsar', fare: 7000 },
      { destination: 'Dalhousie', fare: 9000 },
      { destination: 'Delhi Airport', fare: 6000 },
      { destination: 'Delhi', fare: 6000 },
      { destination: 'Kasol', fare: 9000 },
      { destination: 'Khatu Shyam', fare: 12000 }
    ],
    patiala: [
      { destination: 'Agra', fare: 11000 },
      { destination: 'Amarnath', fare: 16000 },
      { destination: 'Amritsar', fare: 7000 },
      { destination: 'Amritsar Sahib', fare: 7000 },
      { destination: 'Dalhousie', fare: 12000 },
      { destination: 'Dharamshala', fare: 12000 },
      { destination: 'Golden Temple Amritsar', fare: 7000 },
      { destination: 'Ayodhya', fare: 24000 },
      { destination: 'Badrinath Kedarnath', fare: 25000 },
      { destination: 'Char Dham', fare: 25000 },
      { destination: 'Govind Ghat', fare: 24000 },
      { destination: 'Haridwar', fare: 7000 }
    ]
  };

  const routes = cityRoutes?.[cityName?.toLowerCase()] || cityRoutes?.amritsar;
  const formattedCityName = cityName?.charAt(0)?.toUpperCase() + cityName?.slice(1)?.toLowerCase();

  const handleViewCabs = (destination) => {
    const destSlug = destination?.toLowerCase()?.replace(/\s+/g, '-');
    navigate(`/tour-package-booking/${cityName?.toLowerCase()}/${destSlug}`);
  };

  return (
    <>
      <SEO
        title={`Taxi Tour Packages From ${formattedCityName} - Advait Tour and Travels`}
        description={`Explore premium taxi tour packages from ${formattedCityName}. Comfortable travel, professional drivers, and affordable rates.`}
        keywords={`taxi tour packages from ${cityName}, ${cityName} tour packages, ${cityName} taxi service`}
        canonicalUrl={`${window.location?.origin}/tour-packages/${cityName}`}
        ogTitle={`Taxi Tour Packages From ${formattedCityName} - Advait Tour and Travels`}
        ogDescription={`Explore premium taxi tour packages from ${formattedCityName}. Comfortable travel, professional drivers, and affordable rates.`}
        ogUrl={`${window.location?.origin}/tour-packages/${cityName}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          "name": "Advait Tour and Travels",
          "description": `Taxi tour packages from ${formattedCityName}`
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-16 mb-12">
            <div className="container mx-auto px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white text-center">
                Taxi Tour Packages From {formattedCityName}
              </h1>
            </div>
          </div>

          {/* Route Cards */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {routes?.map((route, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6"
                >
                  <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">
                    {formattedCityName} To {route?.destination}
                  </h3>
                  <p className="text-gray-600 text-center mb-4">
                    Fare Starts ₹{route?.fare?.toLocaleString('en-IN')}/-
                  </p>
                  <button
                    onClick={() => handleViewCabs(route?.destination)}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors duration-300"
                  >
                    VIEW CABS
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Visitor's Guide Content */}
          <div className="container mx-auto px-4 mb-16">
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-orange-500 mb-6 text-center">
                Visitor's Guide
              </h2>
              
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <h3 className="text-2xl font-bold text-gray-800">
                  Taxi Tour Packages From {formattedCityName} – Explore Comfort & Convenience with Advait Tour and Travels
                </h3>
                
                <p>
                  Advait Tour and Travels provides Taxi Tour Packages From {formattedCityName} for {formattedCityName} residents with premium tour packages that deliver premium taxi services in {formattedCityName} for individual travelers, families, and groups. All travelers benefit from frequent trips to religious sites along with corporate transportation because our organization utilizes professional drivers to operate well-maintained vehicles. Our travel services adjust to flexible requirements that promote affordable options together with secure travel and dependable service. Our expertise from years of serving the industry allows us to deliver exceptional taxi tour services alongside premium customer satisfaction.
                </p>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Best Taxi Tour Packages From {formattedCityName}
                </h3>
                
                <p>
                  Our business provides well-organized Taxi Tour Packages From {formattedCityName} which lead tourists to Shimla, Manali, Dharamshala, Amritsar, Jaipur, Delhi, as well as other neighboring locations. Our company delivers taxi transportation services to tourists and business travelers while serving customers from the same region who need a trustworthy travel companion. Our travel services provide comfortable transportation to clients at affordable rates while we focus on being on time for every trip so clients experience memorable journeys.
                </p>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Popular Taxi Tour Destinations
                </h3>
                
                <ul className="space-y-3 list-disc list-inside">
                  <li><strong>Shimla & Manali Tour</strong> – Enjoy the mesmerizing beauty of the Himalayas with our comfortable taxis. We tailor-made schedules to give clients the optimal sightseeing experience throughout these picturesque destinations.</li>
                  <li><strong>The Amritsar Golden Temple Tour</strong> - It provides visitors with a sacred pilgrimage experience to the Sikh religion along with visits to Jallianwala Bagh and Wagah Border for an immersive cultural adventure.</li>
                  <li><strong>Jaipur & Rajasthan Tour</strong> – Experience the rich culture and heritage of Rajasthan. Our excursion consists of visiting Hawa Mahal followed by exploring City Palace then moving to Amber Fort and completing the tour with Jantar Mantar.</li>
                  <li><strong>Vaishno Devi Tour</strong> – A comfortable and safe pilgrimage experience with stops at Katra and nearby spiritual attractions.</li>
                  <li><strong>Delhi Tour</strong> - Visitors to Delhi can discover the significant cultural landmarks of the Indian capital including a tour of India Gate alongside the Red Fort and Qutub Minar and Lotus Temple.</li>
                  <li><strong>The Himachal Adventure Tour</strong> - It offers expedition and adventure services leading to various parts of Himachal Pradesh. We structure travel packages that suit people interested in paragliding river rafting and skiing.</li>
                  <li><strong>{formattedCityName} to Leh-Ladakh Tour</strong> – Travel from Leh-Ladakh to witness the astonishing natural vistas as you tour Nubra Valley and Pangong Lake while crossing Khardung La Pass.</li>
                  <li><strong>Udaipur and Jodhpur Tour</strong> - A tour of Udaipur and Jodhpur will take you through Rajasthan to see remarkable palaces, historic sites and beautiful lakes in the region.</li>
                  <li><strong>Agra & Mathura Tour</strong> – Visit the Taj Mahal, Agra Fort, and the sacred city of Mathura, the birthplace of Lord Krishna.</li>
                </ul>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Wide Range of Vehicles to Suit Your Travel Needs
                </h3>
                
                <p>
                  Advait Tour and Travels offers different vehicles which precisely fulfill your travel needs. The fleet at our company spans from individual vehicles to large vehicles which accommodate every type of group.
                </p>

                <h4 className="text-xl font-bold text-gray-800 mt-6">Our Fleet Includes:</h4>
                
                <ul className="space-y-2 list-disc list-inside">
                  <li><strong>One-Way Taxi From {formattedCityName}</strong> – Hassle-free, one-way drop services at competitive rates with multiple vehicle options.</li>
                  <li><strong>Tempo Traveller From {formattedCityName}</strong> – Ideal for family and group travel with comfortable seating and spacious interiors.</li>
                  <li><strong>12-Seater Tempo Traveller</strong> - The 12-seater Tempo Traveller suits groups of moderate size by providing features like modern conveniences as well as enhanced space.</li>
                  <li><strong>17-Seater Tempo Traveller</strong> - Long journeys using our 17-Seater Tempo Traveller provide both space and fully equipped features to ensure peaceful travel conditions.</li>
                  <li><strong>20-Seater Tempo Traveller</strong> - Large families together with corporate teams find the 20-Seater Tempo Traveller ideal because of its superior seating arrangement.</li>
                  <li><strong>Force Urbania From {formattedCityName}</strong> - The Force Urbania From {formattedCityName} provides superb travel services through luxurious seating options alongside AC features and extended seat legroom features.</li>
                  <li><strong>16-Seater Force Urbania</strong> - Corporate occasions, pilgrimage tours, and group vacations will find the 16-Seater Force Urbania most suitable because it provides modern interior features and entertainment systems.</li>
                  <li><strong>Luxury Cars for Special Occasions</strong> - The vehicle portfolio includes lavish sedans and SUVs from BMW Audi and Mercedes which serve guests for weddings, corporate encounters, and VIP services.</li>
                  <li><strong>AC & Non-AC Buses</strong> - The AC & Non-AC bus products from the company serve the transportation needs of schools and corporations while offering affordable comfort during family gatherings and educational outings.</li>
                </ul>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Why Choose Advait Tour and Travels?
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">1. Well-Maintained Vehicles</h4>
                    <p>Regular maintenance inspections of our vehicles guarantee comfortable travel conditions for every member of our passenger group. Modern equipment on each vehicle includes GPS navigation systems and charging ports together with entertainment systems.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">2. Professional & Experienced Drivers</h4>
                    <p>The drivers maintain excellent training together with courteous manners while keeping full destination and route knowledge for delivering effortless journeys. The safety of passengers together with their comfort remains their top priority during the entire trip duration.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">3. Affordable Pricing</h4>
                    <p>The Taxi Tour Packages From {formattedCityName} operate at competitive rates which allows anyone to afford travel while maintaining first-class standards. The company provides clear pricing with no surprise costs so customers can feel safe about the costs involved.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">4. Flexible Booking Options</h4>
                    <p>Our online booking portal lets customers reserve their transportation and they can also contact our staff through 24-hour customer support service. Our customers benefit from momentary verification with adaptable payment alternatives.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">5. Safety & Comfort Guaranteed</h4>
                    <p>Our priority is your safety. Our company maintains strict security protocols while supplying every vehicle with the necessary traveling equipment. Our refuse cars get safety checks and complete sanitization routines for maintaining hygienic conditions before each trip.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">6. Customizable Tour Packages</h4>
                    <p>Our services deliver personalized tour packages according to your choices for an individualized exploration. Our company creates complete tour solutions that adapt to all kinds of sightseeing needs as well as adventure trips and religious expeditions.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">7. 24/7 Customer Support</h4>
                    <p>The travel support team operates on demand for both travel queries and bookings about 24 hours a day. Our services operate with perfect fluidity and we respond immediately to address any requirement our customers may have.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">8. Experienced Travel Consultants</h4>
                    <p>Traditional travel consultants at our company help clients design ideal travel plans that meet their specific interests, financial goals and temporal constraints. Our team helps you make recommendations as well as provides amazing sightseeing activities and recommends ideal travel routes that improve your overall experience.</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">9. Easy Cancellation & Refund Policies</h4>
                    <p>Our business recognizes that travel arrangements might need alterations. The customers of our company enjoy flexible booking policies that provide peace of mind through easy cancellations and refunds.</p>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-800 mt-8">
                  Book Your Taxi Tour Package Today
                </h3>
                
                <p>
                  Advait Tour and Travels enables customers to create easy travel plans through their Taxi Tour Packages From {formattedCityName}. Our commitment to exceptional service ensures that every trip from short to long vacation experience receives premier treatment for better exploration. Book your desired taxi or tempo travel today by contacting us and experience a comfortable high-end exploration of your chosen destination.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300"
              >
                <Phone className="w-6 h-6" />
                +91-7998666660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TourPackageCity;